import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { fetchSharedList, saveFavoriteList } from "../api/getApi";
import { useAuth } from "../context/authContext";
import { getRankKey, classifyRisk } from "../actions/actions";
import Text from "../components/text";

const SharedList = () => {
  const { userId, favoriteId } = useParams();
  const location = useLocation();
  const [listData, setListData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Get notification message from location state, if any
  const message = location.state?.message;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchList = async () => {
      try {
        setLoading(true);
        const response = await fetchSharedList(userId, favoriteId);
        
        if (response.success) {
          setListData(response.listData);
        } else {
          setError(response.error || "Failed to load the shared list");
        }
      } catch (error) {
        console.error("Error:", error);
        setError("This shared list could not be loaded. It may have been removed or is not available.");
      } finally {
        setLoading(false);
      }
    };

    if (userId && favoriteId) {
      fetchList();
    }
  }, [userId, favoriteId]);

  const handleSaveList = async () => {
    if (!isAuthenticated) {
      navigate("/login", {
        state: {
          pendingFavorite: listData,
          redirectAfterLogin: "/profile",
          action: "save",
          message: "Please log in to save this list to your account"
        }
      });
      return;
    }

    try {
      setIsSaving(true);
      const result = await saveFavoriteList(listData);
      
      if (result.success) {
        navigate("/profile", {
          state: { message: "List saved successfully to your account!" }
        });
      } else {
        alert("Failed to save the list: " + (result.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error saving list:", error);
      alert("An error occurred while saving the list");
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleGeneratePdf = async () => {
    if (!isAuthenticated) {
      navigate("/login", {
        state: {
          pendingFavorite: listData,
          redirectAfterLogin: `/shared/${userId}/${favoriteId}`,
          action: "download",
          shareParams: { userId, favoriteId },
          message: "Please log in to download this list as PDF"
        }
      });
      return;
    }

    try {
      setIsGeneratingPdf(true);
      
      navigate("/printpdf", {
        state: {
          selectedColleges: listData.colleges,
          formData: listData.formData,
          listName: listData.name
        }
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF");
      setIsGeneratingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mb-4"></div>
        <h3 className="text-lg font-medium text-gray-800">{Text.SharedList.Loading}</h3>
        <p className="text-gray-500 mt-2">{Text.SharedList.PleaseWait}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-grow container mx-auto px-4 py-8 flex flex-col items-center justify-center">
          <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Error Loading List</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate("/")}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!listData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-orange-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{Text.SharedList.ListNotFound}</h2>
          <p className="text-gray-600 mb-6">{Text.SharedList.ListNotFoundDescription}</p>
          <button
            onClick={() => navigate("/")}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const rankKey = getRankKey(listData.formData || {});

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Page Header */}
        <div className="flex items-center mb-6">
          <Link to="/" className="text-blue-600 hover:text-blue-800 mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Shared College List</h1>
        </div>

        {/* Notification */}
        {message && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mt-0.5 mr-3" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-blue-700">{message}</p>
          </div>
        )}

        {/* Main Content - Modified for mobile-first ordering */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* ===== RIGHT CONTENT FIRST (for mobile) ===== */}
          {/* Student Information and College List - This appears first on mobile */}
          <div className="order-1 lg:order-2 lg:w-3/4">
            {/* Student Information Card */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                Student Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Rank</p>
                  <p className="text-xl font-semibold text-gray-800">{listData.formData?.rank || "Not provided"}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Gender</p>
                  <p className="text-xl font-semibold text-gray-800">{listData.formData?.gender || "Not provided"}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Category</p>
                  <p className="text-xl font-semibold text-gray-800">{(listData.formData?.caste || "Not provided").toUpperCase()}</p>
                </div>
              </div>
            </div>
            
            {/* College List Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                </svg>
                Selected Colleges ({listData.colleges?.length || 0})
              </h2>

              {listData.colleges && listData.colleges.length > 0 ? (
                <div className="overflow-hidden border border-gray-200 rounded-lg">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">College</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Location</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {listData.colleges.map((college, index) => {
                          const collegeRank = parseInt(college[rankKey]) || 0;
                          const studentRank = parseInt(listData.formData?.rank) || 0;
                          const riskClass = classifyRisk(studentRank, collegeRank);
                          
                          // Map risk class to appropriate style
                          const riskStyles = {
                            'safety-safe': 'bg-green-50 border-l-4 border-green-400',
                            'safety-moderate': 'bg-yellow-50 border-l-4 border-yellow-400',
                            'safety-ambitious': 'bg-red-50 border-l-4 border-red-400',
                            'unknown': ''
                          };
                          
                          const riskStyle = riskStyles[riskClass] || '';
                          
                          return (
                            <tr key={index} className={riskStyle}>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{college.inst_code}</td>
                              <td className="px-4 py-3 text-sm text-gray-700 hidden md:table-cell">
                                <div className="max-w-xs truncate">{college.inst_name}</div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 hidden md:table-cell">{college.place}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{college.branch_code}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{college[rankKey]}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">{Text.SharedList.NoColleges}</h3>
                  <p className="text-gray-500 max-w-md mx-auto">{Text.SharedList.NoCollegesDescription}</p>
                </div>
              )}
            </div>
            
            {/* Risk Legend */}
            {listData.colleges && listData.colleges.length > 0 && (
              <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Legend</h3>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center">
                    <span className="w-4 h-4 bg-green-400 rounded-full mr-2"></span>
                    <span className="text-sm text-gray-600">Safe Choice (College rank &gt; Your rank)</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-4 h-4 bg-yellow-400 rounded-full mr-2"></span>
                    <span className="text-sm text-gray-600">Moderate Risk (±5% of your rank)</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-4 h-4 bg-red-400 rounded-full mr-2"></span>
                    <span className="text-sm text-gray-600">Ambitious (College rank &lt; Your rank)</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* ===== LEFT SIDEBAR SECOND (for mobile) ===== */}
          {/* List Information and Actions - This appears second on mobile */}
          <div className="order-2 lg:order-1 lg:w-1/4 mt-8 lg:mt-0">
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-100 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                </svg>
                List Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium text-gray-800">{listData.name || "Unnamed List"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created On</p>
                  <p className="font-medium text-gray-800">{new Date(listData.savedAt).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Colleges</p>
                  <p className="font-medium text-gray-800">{listData.colleges?.length || 0} colleges</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Exam Type</p>
                  <p className="font-medium text-gray-800">{(listData.formData?.examType || 'AP').toUpperCase()} EAMCET</p>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                Actions
              </h2>
              
              <div className="space-y-3">
                <button
                  onClick={handleSaveList}
                  disabled={isSaving}
                  className="w-full flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                      </svg>
                      Save to My Lists
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleGeneratePdf}
                  disabled={isGeneratingPdf}
                  className="w-full flex items-center justify-center px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isGeneratingPdf ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                      </svg>
                      Download as PDF
                    </>
                  )}
                </button>
                
                <Link to="/" className="w-full flex items-center justify-center px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedList;
